import { h, icon } from './dom.js';
import { progress } from './store.js';

/** One colour per title, so a poster that has no picture still looks deliberate. */
export function hueOf(title) {
  let hue = 0;
  for (const ch of String(title)) hue = (hue * 31 + ch.charCodeAt(0)) % 360;
  return hue;
}

const initials = (title) => String(title).split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

/** The poster picture with its badges. A cover that is missing or will not load falls back to the title's initials. */
export function poster(d, ctx, { badges = true } = {}) {
  const { design } = ctx;
  const box = h('div', { class: 'poster', style: `--hue:${hueOf(d.title)}` }, h('div', { class: 'ph' }, initials(d.title)));
  if (d.cover) {
    const img = h('img', { src: d.cover, alt: '', loading: 'lazy', decoding: 'async', referrerpolicy: 'no-referrer' });
    img.addEventListener('error', () => img.remove());
    box.append(img);
  }
  if (badges) {
    if (d.h265Episodes > 0) box.append(h('span', { class: 'badge h265' }, ctx.text.t('h265_badge')));
    if (design['browse.rating'] && typeof d.rating === 'number' && d.rating > 0) box.append(h('span', { class: 'badge rate' }, '★ ' + d.rating.toFixed(1)));
    if (design['browse.badge'] && d.episodes) box.append(h('span', { class: 'badge eps' }, `${d.episodes} ${ctx.text.t(d.episodes === 1 ? 'episode' : 'episodes').toLowerCase()}`));
  }
  return box;
}

export function card(d, ctx, { href, sub } = {}) {
  return h(
    'a',
    { class: 'card', href: href ?? ctx.href(`/drama/${d.id}`), title: d.title },
    poster(d, ctx),
    ctx.design['cinema.cover_titles'] ? h('div', { class: 'title' }, d.title) : null,
    sub ? h('div', { class: 'sub' }, sub) : null,
  );
}

/**
 * Cards in a grid, with an ad card after every N when the spot is filled. `before` is how many cards a list already showed
 * (the pages loaded earlier), so the ads keep the same rhythm all the way down a list that grows.
 */
export function cards(items, ctx, { adSpot, hrefFor, subFor, before = 0 } = {}) {
  const every = adSpot ? ctx.ads.everyN(adSpot) : null;
  const out = [];
  items.forEach((d, i) => {
    out.push(card(d, ctx, { href: hrefFor?.(d), sub: subFor?.(d) }));
    if (every && (before + i + 1) % every === 0 && i + 1 < items.length) {
      const ad = ctx.ads.slot(adSpot, { card: true });
      if (ad) out.push(ad);
    }
  });
  return out;
}

export function grid(items, ctx, opts) {
  return h('div', { class: 'grid' }, cards(items, ctx, opts));
}

/**
 * A sideways-scrolling row. It has no scroll bar: a finger or a trackpad slides it, a mouse can drag it, and computers get arrows
 * (when the site keeps them on). Links inside stay reachable with the keyboard, which scrolls the row to them.
 */
export function row(items, ctx, opts) {
  const strip = h('div', { class: 'row' }, cards(items, ctx, opts));
  const wrap = h('div', { class: `row-wrap ${ctx.design['cinema.arrows'] ? 'arrows' : ''}` }, strip);
  const step = (dir) => strip.scrollBy({ left: dir * strip.clientWidth * 0.85, behavior: 'smooth' });
  if (ctx.design['cinema.arrows']) {
    wrap.append(
      h('button', { class: 'arrow left', type: 'button', 'aria-label': ctx.text.t('previous_episode'), on: { click: () => step(-1) } }, icon('left')),
      h('button', { class: 'arrow right', type: 'button', 'aria-label': ctx.text.t('next_episode'), on: { click: () => step(1) } }, icon('right')),
    );
  }
  dragToSlide(strip);
  return wrap;
}

/** Lets a mouse drag a row sideways, as a finger does. A drag is not a click: the card under the pointer is not opened by it. */
function dragToSlide(strip) {
  let start = null;
  let dragged = false;
  const move = (e) => {
    const dx = e.clientX - start.x;
    if (!dragged && Math.abs(dx) < 5) return;
    dragged = true;
    strip.classList.add('dragging');
    strip.scrollLeft = start.left - dx;
  };
  // Listening on the window only while a drag lasts, so rows that come and go leave nothing behind.
  const end = () => {
    start = null;
    strip.classList.remove('dragging');
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', end);
    window.removeEventListener('pointercancel', end);
  };
  strip.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    start = { x: e.clientX, left: strip.scrollLeft };
    dragged = false;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  });
  strip.addEventListener('dragstart', (e) => e.preventDefault());
  strip.addEventListener(
    'click',
    (e) => {
      if (dragged) {
        e.preventDefault();
        e.stopPropagation();
        dragged = false;
      }
    },
    true,
  );
}

export function section(title, body, { more } = {}) {
  return h('section', { class: 'section' }, h('div', { class: 'section-head' }, h('h2', {}, title), more ? h('a', { href: more.href }, more.label) : null), body);
}

export function message(ctx, kind, retry) {
  const text = kind === 'empty' ? ctx.text.t('no_results') : ctx.text.t('error');
  return h('div', { class: 'state' }, h('p', {}, text), retry ? h('button', { class: 'btn', type: 'button', on: { click: retry } }, ctx.text.t('retry')) : null);
}

/** "Continue watching": where this browser stopped, newest first. */
export function continueItems() {
  return progress.recent(12).filter((p) => p.title);
}
